import React, { useEffect, useState, useRef } from "react";
import { fetchLocalocalexpertcitylist } from "../services/Localocalexpertcitylist";
import { API_BASE } from "../api/apiConfig";

const Localocalexpertcitylist = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("city");

  const sliderRef = useRef(null);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const result = await fetchLocalocalexpertcitylist();

        if (!result || !result.status || !Array.isArray(result.data)) {
          setError("Failed to load cities");
          return;
        }

        const allCities = result.data
          .filter((item) => Array.isArray(item.city))
          .flatMap((item) =>
            item.city.map((c) => ({
              id: c.id,
              name: c.city,
              expert_count: c.expert_count,
              title: c.title,
              image: c.cover_image
                ? API_BASE + c.cover_image
                : API_BASE + "default.jpg",
            }))
          );

        setCities(allCities);
      } catch {
        setError("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    loadCities();
  }, []);

  const scrollNext = () => sliderRef.current.scrollBy({ left: 380, behavior: "smooth" });
  const scrollPrev = () => sliderRef.current.scrollBy({ left: -380, behavior: "smooth" });

  if (loading) return <div className="text-center p-6">Loading cities…</div>;
  if (error) return <div className="text-center text-red-600 bg-red-100 p-4 rounded">{error}</div>;

  return (
    <section className="relative w-full bg-white py-10 md:py-24 expert-journey-section">
      <div className="absolute left-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl animate-pulse-soft" />

      <div className="container mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="mb-5">
          <div className="inline-flex items-center rounded-full border border-green-100 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
            • Browse Experts
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Find the <span className="text-green-600">Perfect Expert</span> for Your Journey
            </h2>
            <p className="text-base text-gray-600">
              Discover travel experts by city or specialty to get personalized guidance for your next adventure.
            </p>
          </div>

          <div className="w-full md:w-[280px] bg-gray-100 rounded-full px-4 py-2 flex items-center">
            <input
              placeholder="Search experts..."
              className="bg-transparent text-gray-800 placeholder:text-gray-500 border-0 outline-none flex-1 text-sm"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="h-10 grid w-full max-w-md mx-auto mb-8 grid-cols-2 items-center rounded-md bg-muted p-1">
          <button
            onClick={() => setActiveTab("city")}
            className={`rounded-sm px-3 py-1.5 text-sm font-medium ${
              activeTab === "city" ? "bg-white shadow" : "hover:text-black"
            }`}
          >
            Browse by City
          </button>
          <button
            onClick={() => setActiveTab("specialty")}
            className={`rounded-sm px-3 py-1.5 text-sm font-medium ${
              activeTab === "specialty" ? "bg-white shadow" : "hover:text-black"
            }`}
          >
            Browse by Specialty
          </button>
        </div>

        {/* CITY TAB */}
        {activeTab === "city" && (
          <div className="relative px-10">
            <button
              onClick={scrollPrev}
              className="customPrevBtn absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border bg-white shadow hover:scale-110"
            >
              ‹
            </button>
            <div
              ref={sliderRef}
              className="flex gap-4 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory w-full"
            >
              {cities.map((city) => (
                <div
                  key={city.id}
                  className="min-w-[280px] max-w-[350px] snap-start bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
                >
                  <div className="relative h-40">
                    <img src={city.image} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 text-white font-bold"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin h-3.5 w-3.5 mr-1.5"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path><circle cx="12" cy="10" r="3"></circle></svg> {city.name}</div>
                  </div>

                  <div class="p-4">
                      <div class="text-green-600 text-xs font-medium mb-2">{city.expert_count} experts available</div>
                      <div class="mb-4">
                          <div class="text-xs text-gray-500 mb-1">Popular:</div>
                          <div class="font-medium text-gray-900 text-xs">{city.title}</div>
                      </div>
                      <a href="/experts/jaipur"
                          ><button
                              class="inline-flex items-center justify-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-background hover:text-accent-foreground px-4 w-full border-green-600 text-green-600 hover:bg-green-50 hover:border-green-700 font-medium py-1.5 text-xs h-auto"
                          >
                              View Experts in {city.name} →
                          </button></a
                      >
                  </div>
                </div>
              ))}
            </div>
            <button
                onClick={scrollNext}
                className="customNextBtn absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border bg-white shadow hover:scale-110"
              >
                ›
              </button>

          </div>
        )}

        {/* SPECIALTY TAB (static for now) */}
        {activeTab === "specialty" && (
          <div className="text-center text-gray-500 py-10">
            Specialty content can go here later 🙂
          </div>
        )}
      </div>
    </section>
  );
};

export default Localocalexpertcitylist;
